import { Box, Stack, Typography } from "@mui/material";
import { CertificateSection } from "App/template/Certificate/Section";
import { RealizationCertificate } from "../../model";
import {
  SIZE_CATEGORY_LABELS,
} from "@tracabois/domain";

export interface RealizationDetailSectionProps {
  item: RealizationCertificate;
}

const getEnumName = (value: any): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && value.name) return value.name;
  return String(value);
};

interface DetailRowProps {
  label: string;
  value?: string | number | null;
}

const DetailRow = ({ label, value }: DetailRowProps) => {
  if (value == null || value === "") return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5, py: 0.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label} :
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
};

interface DetailGroupProps {
  title: string;
  children: React.ReactNode;
}

const DetailGroup = ({ title, children }: DetailGroupProps) => (
  <Box>
    <Typography
      variant="subtitle2"
      color="secondary"
      sx={{
        textTransform: "uppercase",
        letterSpacing: 0.5,
        mb: 0.5,
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 0.25,
      }}
    >
      {children}
    </Box>
  </Box>
);

export const RealizationDetailSection = (
  props: RealizationDetailSectionProps
) => {
  const { item } = props;

  const buildingTypeName = getEnumName(item.buildingType);
  const ownershipTypeName = getEnumName(item.ownershipType);
  const sizeCategoryName = getEnumName(item.sizeCategory);

  const hasLocalisation =
    item.commune || item.codePostal || item.codeInsee || item.departement;

  const hasBatiment =
    buildingTypeName ||
    ownershipTypeName ||
    item.deliveryYear ||
    sizeCategoryName;

  const hasSurfaces =
    item.surface ||
    item.surfaceRetained ||
    item.volume ||
    item.volumeDescription;

  const hasData = hasLocalisation || hasBatiment || hasSurfaces;

  if (!hasData) return null;

  return (
    <CertificateSection title="Informations du projet">
      <Box
        sx={{
          bgcolor: "#f9f7f5",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Stack spacing={2}>
          {hasLocalisation && (
            <DetailGroup title="Localisation">
              <DetailRow label="Commune" value={item.commune} />
              <DetailRow label="Code postal" value={item.codePostal} />
              <DetailRow label="Code INSEE" value={item.codeInsee} />
              <DetailRow label="Département" value={item.departement} />
            </DetailGroup>
          )}
          {hasBatiment && (
            <DetailGroup title="Bâtiment">
              <DetailRow
                label="Type de bâtiment"
                value={buildingTypeName}
              />
              <DetailRow
                label="Type de propriété"
                value={ownershipTypeName}
              />
              <DetailRow
                label="Année de réalisation"
                value={item.deliveryYear}
              />
              <DetailRow
                label="Catégorie de taille"
                value={
                  SIZE_CATEGORY_LABELS[sizeCategoryName] || sizeCategoryName
                }
              />
            </DetailGroup>
          )}
          {hasSurfaces && (
            <DetailGroup title="Surfaces & Volumes">
              <DetailRow
                label="Surface"
                value={item.surface ? `${item.surface} m²` : undefined}
              />
              <DetailRow
                label="Surface retenue"
                value={
                  item.surfaceRetained
                    ? `${item.surfaceRetained} m²`
                    : undefined
                }
              />
              <DetailRow
                label="Volume"
                value={item.volume ? `${item.volume} m³` : undefined}
              />
              <DetailRow label="Volume certifié" value={item.volumeDescription} />
            </DetailGroup>
          )}
        </Stack>
      </Box>
    </CertificateSection>
  );
};
